import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Tabs, Tab, Grid, Card, CardContent,
  Switch, Divider, FormControl, Select, MenuItem, IconButton, Tooltip, alpha,
  CircularProgress, Avatar,
} from '@mui/material';
import {
  CheckCircle, Cancel, Visibility, Business, Settings, Category, Search,
  Refresh, Warning, Flag, Star, Home, LocationCity, Apartment, Villa,
  VerifiedUser, Block, TrendingUp, FilterList,
} from '@mui/icons-material';
import { adminAPI, settingsAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

const statusColors = { active: 'success', inactive: 'default', under_review: 'warning', rejected: 'error', suspended: 'error' };

function AdminPropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const [propSettings, setPropSettings] = useState({
    auto_approve: 'false', max_images: '15', min_images: '3',
    require_verification: 'true', max_price: '50000', min_price: '50',
    featured_listing_fee: '100', quality_score_threshold: '60',
    allow_virtual_tours: 'true', require_address_proof: 'true',
  });

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await adminAPI.listProperties(params);
      setProperties(res.data.properties || res.data || []);
      setTotal(res.data.total || res.data.length || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProperties(); }, [page, statusFilter]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await settingsAPI.list({ category: 'property' });
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(s => { map[s.key] = s.value; });
          setPropSettings(prev => ({ ...prev, ...map }));
        }
      } catch (_) {}
    };
    load();
  }, []);

  const handleApprove = async (id) => {
    try {
      await adminAPI.approveProperty(id);
      setSuccess('Property approved');
      fetchProperties();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleReject = async () => {
    if (!rejectDialog || !rejectReason) return;
    try {
      await adminAPI.rejectProperty(rejectDialog, rejectReason);
      setRejectDialog(null); setRejectReason('');
      setSuccess('Property rejected');
      fetchProperties();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleSettingUpdate = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setPropSettings(prev => ({ ...prev, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update setting'); }
  };

  const pendingCount = properties.filter(p => p.status === 'under_review').length;
  const activeCount = properties.filter(p => p.status === 'active').length;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Property Management
          </Typography>
          <Typography variant="body2" color="text.secondary">Review, approve, and manage all listed properties</Typography>
        </Box>
        <IconButton onClick={fetchProperties} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="All Properties" icon={<Business sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Review Settings" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Categories & Quality" icon={<Category sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total', count: total, color: '#6366f1', icon: <Business /> },
              { label: 'Active', count: activeCount, color: '#10b981', icon: <CheckCircle /> },
              { label: 'Pending Review', count: pendingCount, color: '#f59e0b', icon: <Warning /> },
              { label: 'Rejected', count: properties.filter(p => p.status === 'rejected').length, color: '#ef4444', icon: <Cancel /> },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        <Typography variant="h5" fontWeight={800}>{item.count}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" placeholder="Search properties..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchProperties()}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                sx={{ flexGrow: 1 }} />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={fetchProperties} startIcon={<Search />}>Search</Button>
            </Stack>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Property</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Rooms</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : properties.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No properties found</TableCell></TableRow>
                ) : properties.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Box>
                        <Typography fontWeight={600} noWrap sx={{ maxWidth: 220 }}>{p.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.nearby_university}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={p.property_type?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell><Typography variant="body2">{p.city}, {p.region}</Typography></TableCell>
                    <TableCell><Typography variant="body2" fontWeight={600}>{p.currency} {(p.price_per_month || p.price_per_semester || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell><Chip label={`${p.available_rooms}/${p.total_rooms}`} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={p.status?.replace('_', ' ')} size="small" color={statusColors[p.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {p.status === 'under_review' && (
                          <>
                            <Button size="small" color="success" variant="contained" onClick={() => handleApprove(p.id)} sx={{ px: 1.5 }}>Approve</Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => setRejectDialog(p.id)} sx={{ px: 1.5 }}>Reject</Button>
                          </>
                        )}
                        <Tooltip title="View Listing"><IconButton size="small" href={`/properties/${p.id}`} target="_blank"><Visibility fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {total > 15 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={Math.ceil(total / 15)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Approval Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Auto-Approve Properties</Typography><Typography variant="body2" color="text.secondary">Skip manual review for verified landlords</Typography></Box>
                  <Switch checked={propSettings.auto_approve === 'true'} onChange={(e) => handleSettingUpdate('auto_approve', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Require Landlord Verification</Typography><Typography variant="body2" color="text.secondary">Only verified users can list properties</Typography></Box>
                  <Switch checked={propSettings.require_verification === 'true'} onChange={(e) => handleSettingUpdate('require_verification', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Require Address Proof</Typography><Typography variant="body2" color="text.secondary">Landlords must prove property ownership</Typography></Box>
                  <Switch checked={propSettings.require_address_proof === 'true'} onChange={(e) => handleSettingUpdate('require_address_proof', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Allow Virtual Tours</Typography><Typography variant="body2" color="text.secondary">Enable video/360 tour uploads</Typography></Box>
                  <Switch checked={propSettings.allow_virtual_tours === 'true'} onChange={(e) => handleSettingUpdate('allow_virtual_tours', e.target.checked ? 'true' : 'false')} />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Listing Validation Rules</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Minimum Images</Typography>
                  <TextField size="small" type="number" value={propSettings.min_images}
                    onChange={(e) => setPropSettings(p => ({ ...p, min_images: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('min_images', e.target.value)} sx={{ width: 100 }} /></Box>
                <Box><Typography fontWeight={600} gutterBottom>Maximum Images</Typography>
                  <TextField size="small" type="number" value={propSettings.max_images}
                    onChange={(e) => setPropSettings(p => ({ ...p, max_images: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('max_images', e.target.value)} sx={{ width: 100 }} /></Box>
                <Box><Typography fontWeight={600} gutterBottom>Min Price (GH₵)</Typography>
                  <TextField size="small" type="number" value={propSettings.min_price}
                    onChange={(e) => setPropSettings(p => ({ ...p, min_price: e.target.value }))}
                    onKeyDown={decimalOnly}
                    onBlur={(e) => handleSettingUpdate('min_price', e.target.value)} sx={{ width: 120 }} /></Box>
                <Box><Typography fontWeight={600} gutterBottom>Max Price (GH₵)</Typography>
                  <TextField size="small" type="number" value={propSettings.max_price}
                    onChange={(e) => setPropSettings(p => ({ ...p, max_price: e.target.value }))}
                    onKeyDown={decimalOnly}
                    onBlur={(e) => handleSettingUpdate('max_price', e.target.value)} sx={{ width: 120 }} /></Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Property Categories</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  { name: 'Hostel', icon: <Apartment />, color: '#6366f1' },
                  { name: 'Apartment', icon: <Home />, color: '#3b82f6' },
                  { name: 'Single Room', icon: <Villa />, color: '#10b981' },
                  { name: 'Shared Room', icon: <LocationCity />, color: '#f59e0b' },
                  { name: 'Self-Contained', icon: <Business />, color: '#8b5cf6' },
                ].map((cat, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha(cat.color, 0.1), color: cat.color, width: 36, height: 36 }}>{cat.icon}</Avatar>
                        <Typography fontWeight={600}>{cat.name}</Typography>
                      </Stack>
                      <Switch defaultChecked />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Quality Control</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Quality Score Threshold (%)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Properties below this score are flagged for review</Typography>
                  <TextField size="small" type="number" value={propSettings.quality_score_threshold}
                    onChange={(e) => setPropSettings(p => ({ ...p, quality_score_threshold: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('quality_score_threshold', e.target.value)} sx={{ width: 100 }} /></Box>
                <Box><Typography fontWeight={600} gutterBottom>Featured Listing Fee (GH₵)</Typography>
                  <TextField size="small" type="number" value={propSettings.featured_listing_fee}
                    onChange={(e) => setPropSettings(p => ({ ...p, featured_listing_fee: e.target.value }))}
                    onKeyDown={decimalOnly}
                    onBlur={(e) => handleSettingUpdate('featured_listing_fee', e.target.value)} sx={{ width: 120 }} /></Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={600} gutterBottom>Quality Checks</Typography>
                  <Stack spacing={1}>
                    {['Photo quality verification', 'Description length check', 'Amenity completeness', 'Price reasonableness', 'Location accuracy'].map(check => (
                      <Stack key={check} direction="row" alignItems="center" spacing={1}>
                        <CheckCircle color="success" sx={{ fontSize: 18 }} />
                        <Typography variant="body2">{check}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reject Property</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection..." sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={!rejectReason}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminPropertiesPage;
